import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

interface AwsWafCaptchaApi {
  renderCaptcha(
    container: HTMLElement,
    config: {
      apiKey: string;
      onSuccess: (token: string) => void;
      onError: (error: Error) => void;
      onExpired: () => void;
    },
  ): void;
}

declare global {
  interface Window {
    AwsWafCaptcha?: AwsWafCaptchaApi;
  }
}

export interface AwsWafCaptchaInitConfig {
  container: string;
  onSuccess: (token: string) => void;
  onError: (error: Error) => void;
  onExpired: () => void;
}

@Injectable({ providedIn: 'root' })
export class AwsWafCaptchaService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly captchaLoadStatus = new BehaviorSubject<boolean>(false);
  private loadPromise: Promise<AwsWafCaptchaApi> | null = null;

  constructor() {
    if (!this.isBrowser) return;

    if (!environment.production) {
      this.captchaLoadStatus.next(true);
      return;
    }

    void this.waitForCaptcha().catch(() => {
      this.captchaLoadStatus.next(false);
    });
  }

  getCaptchaLoadStatus(): Observable<boolean> {
    return this.captchaLoadStatus.asObservable();
  }

  initCaptcha(config: AwsWafCaptchaInitConfig): void {
    if (!this.isBrowser) return;

    const container = document.getElementById(config.container);
    if (!container) {
      config.onError(new Error(`Captcha container "${config.container}" was not found.`));
      return;
    }

    if (!environment.production) {
      this.renderDebugCaptcha(container, config.onSuccess);
      return;
    }

    void this.waitForCaptcha()
      .then(api => {
        this.zone.runOutsideAngular(() => {
          api.renderCaptcha(container, {
            apiKey: environment.awsWafConfig.apiKey,
            onSuccess: token => this.zone.run(() => config.onSuccess(token)),
            onError: error => this.zone.run(() => config.onError(error)),
            onExpired: () => this.zone.run(() => config.onExpired()),
          });
        });
      })
      .catch(error => {
        config.onError(this.toError(error));
      });
  }

  resetCaptcha(containerId = 'aws-waf-captcha-container'): void {
    if (!this.isBrowser) return;

    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = '';
    }
  }

  private waitForCaptcha(timeoutMs = 10_000, intervalMs = 100): Promise<AwsWafCaptchaApi> {
    if (window.AwsWafCaptcha) {
      this.captchaLoadStatus.next(true);
      return Promise.resolve(window.AwsWafCaptcha);
    }

    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<AwsWafCaptchaApi>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[data-aws-waf-captcha="true"]',
      );
      const scriptUrl = (environment.awsWafConfig.jsApiUrl || '').trim();
      if (!existingScript && (!scriptUrl || scriptUrl.startsWith('REPLACE_WITH_'))) {
        this.captchaLoadStatus.next(false);
        reject(new Error('AWS WAF Captcha JS API URL is not configured.'));
        return;
      }

      const startedAt = Date.now();
      let interval: number | null = null;
      let timeout: number | null = null;

      const cleanup = () => {
        if (interval !== null) window.clearInterval(interval);
        if (timeout !== null) window.clearTimeout(timeout);
        interval = null;
        timeout = null;
      };

      const resolveIfReady = () => {
        if (!window.AwsWafCaptcha) return false;
        cleanup();
        this.captchaLoadStatus.next(true);
        resolve(window.AwsWafCaptcha);
        return true;
      };

      const fail = (error: Error) => {
        cleanup();
        this.loadPromise = null;
        this.captchaLoadStatus.next(false);
        reject(error);
      };

      const script = existingScript ?? document.createElement('script');

      if (!existingScript) {
        script.src = scriptUrl;
        script.async = true;
        script.defer = true;
        script.dataset['awsWafCaptcha'] = 'true';
        script.addEventListener('error', () => {
          fail(new Error('AWS WAF Captcha script failed to load.'));
        });
        document.head.appendChild(script);
      }

      interval = window.setInterval(() => {
        if (resolveIfReady()) return;

        if (Date.now() - startedAt >= timeoutMs) {
          fail(new Error('AWS WAF Captcha script did not load.'));
        }
      }, intervalMs);

      timeout = window.setTimeout(() => {
        fail(new Error('AWS WAF Captcha script did not load.'));
      }, timeoutMs + intervalMs);
    });

    return this.loadPromise;
  }

  private renderDebugCaptcha(container: HTMLElement, onSuccess: (token: string) => void): void {
    container.innerHTML = '';
    const panel = document.createElement('div');
    panel.className = 'debug-captcha-panel';

    const label = document.createElement('span');
    label.textContent = 'Local captcha check';

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Pass captcha';
    button.addEventListener('click', () => {
      onSuccess(`debug-token-${Date.now()}`);
      button.textContent = 'Captcha passed';
      button.disabled = true;
      panel.classList.add('captcha-passed');
    });

    panel.append(label, button);
    container.append(panel);
    this.captchaLoadStatus.next(true);
  }

  private toError(error: unknown): Error {
    if (error instanceof Error) return error;
    return new Error(String(error));
  }
}
